import FormControl from "common/components/FormControl/FormControl";
import RadioBlock from "common/components/RadioBlock/RadioBlock";
import RadioBlockItem from "common/components/RadioBlockItem/RadioBlockItem";
import useList from "hooks/useList";
import { useMemo, useState } from "react";
import { Field } from "react-final-form";
import { useTranslation } from "react-i18next";
import { isPasswordCorrect } from "common/components/Password/Password";
import ToggleSwitch from "common/components/ToggleSwitch/ToggleSwitch";
import { checkEmail } from "common/utils/fieldsValidators";
import CrudForm from "common/components/Crud/CrudForm";
import type { Plan, Subscription, User } from "types";
import type { IconNames } from "components/Icon/Icon";
import PlanCheckboxList from "components/select/PlanSelect/PlanCheckboxList";
import OrganizationSelect from "components/select/OrganizationSelect/OrganizationSelect";

const SUBSCRIPTION_ICONS: IconNames[] = ["BsStar", "BsStarHalf", "BsStarFill"];
const SECONDARY_COLOR = "#009FED";
const TERNARY_COLOR = "#98CC33";

const interpolateColor = (t: number) => {
  const r = Math.round(0x00 + t * (0x98 - 0x00));
  const g = Math.round(0x9f + t * (0xcc - 0x9f));
  const b = Math.round(0xed + t * (0x33 - 0xed));
  return `#${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
};

type Props = {
  onSubmit: (values: any) => any;
  onCancel?: () => void;
  item?: User;
  organizationId?: number;
  organizationSubscriptionId?: number;
  organizationPlansIds?: number[];
};

function OrganizationUserForm({
  onSubmit,
  onCancel,
  item,
  organizationId,
  organizationSubscriptionId,
  organizationPlansIds,
}: Props) {
  const { t } = useTranslation();
  const [changePassword, setChangePassword] = useState(false);
  const { items: allSubscriptions } = useList<Subscription>("subscriptions");
  const { items: allPlans } = useList<Plan>("plans");

  // Subscriptions allowed: id <= org subscription id
  const allowedSubscriptions = useMemo(() => {
    const list = allSubscriptions ?? [];
    if (!organizationSubscriptionId) return list;
    return list.filter((s) => s.id <= organizationSubscriptionId);
  }, [allSubscriptions, organizationSubscriptionId]);

  // Icon/color based on position in the FULL list for consistency
  const subscriptionMeta = useMemo(() => {
    const list = allSubscriptions ?? [];
    return list.reduce((acc, s, index) => {
      const ratio = list.length > 1 ? index / (list.length - 1) : 0;
      const color =
        index === 0
          ? SECONDARY_COLOR
          : index === list.length - 1
          ? TERNARY_COLOR
          : interpolateColor(ratio);
      acc[s.id] = { icon: SUBSCRIPTION_ICONS[index] ?? "BsStarFill", color };
      return acc;
    }, {} as Record<number, { icon: IconNames; color: string }>);
  }, [allSubscriptions]);

  const subscriptionItems = useMemo(
    () =>
      allowedSubscriptions.map((s) => ({
        id: s.id,
        title: s.name,
        subtitle: s.description,
        icon: subscriptionMeta[s.id]?.icon ?? "BsStar",
        color: subscriptionMeta[s.id]?.color ?? SECONDARY_COLOR,
      })),
    [allowedSubscriptions, subscriptionMeta],
  );

  const initialValues = useMemo(
    () =>
      item?.id
        ? {
            ...item,
            organization: item.organization?.id,
            plans: organizationId
              ? item?.plans
                  ?.filter((p) => organizationPlansIds?.includes(p.id))
                  .map((p) => p.id)
              : item?.plans?.map((p) => p.id),
            subscription: item.subscription?.id,
            email: item?.email?.includes("fakemail.com")
              ? ""
              : item?.email || "",
          }
        : {
            username: undefined,
            email: "",
            organization: organizationId,
            name: undefined,
            password: undefined,
            role: 1,
          },
    [item, organizationId, organizationPlansIds],
  );

  const checkPassword = (value: string) =>
    isPasswordCorrect(value) ? undefined : t("components.password.tooWeak");

  return (
    <CrudForm
      onSubmit={onSubmit}
      onCancel={onCancel}
      initialValues={initialValues}
      headerIcon="UserIcon"
      i18nNamespace="users"
      render={() => (
        <div className="white-box flex flex-col gap-4 mt-2">
          <div className="flex flex-row gap-4">
            <FormControl
              name="username"
              title={t("users.name")}
              required
              containerStyle="flex-1"
            />
            <FormControl
              name="email"
              title={t("users.email")}
              validate={(val: string) => checkEmail(val, t, false)}
              containerStyle="flex-1"
            />
          </div>

          {item?.id ? (
            <>
              <ToggleSwitch
                rightLabel={t("users.newPassword")}
                value={changePassword}
                onChange={setChangePassword}
              />
              {changePassword && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <FormControl
                    type="jpassword"
                    name="password"
                    title={t("users.password")}
                    helpText={t("users.passwordInstruction")}
                    required
                    validate={checkPassword}
                  />
                  <FormControl
                    name="confirmPassword"
                    title={t("users.confirmPassword")}
                    type="password"
                  />
                </div>
              )}
            </>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <FormControl
                type="jpassword"
                name="password"
                title={t("users.password")}
                helpText={t("users.passwordInstruction")}
                required
                validate={checkPassword}
              />
              <FormControl
                name="confirmPassword"
                title={t("users.confirmPassword")}
                type="password"
              />
            </div>
          )}

          {subscriptionItems.length > 0 && (
            <Field name="subscription" defaultValue={subscriptionItems[0]?.id}>
              {({ input }) => (
                <div>
                  <span className="text-sm font-bold text-slate-600">
                    {t("organizations.subscription")}
                  </span>
                  <RadioBlock
                    items={subscriptionItems}
                    value={input.value || undefined}
                    onChange={input.onChange}
                    Component={RadioBlockItem}
                    horizontal
                  />
                </div>
              )}
            </Field>
          )}

          <FormControl
            name="plans"
            title={t("organizations.plans")}
            type="custom"
          >
            <PlanCheckboxList
              filters={
                organizationId
                  ? organizationPlansIds?.map((id, index) => ({
                      name: `id][$in][${index}`,
                      value: id,
                    })) ?? []
                  : undefined
              }
            />
          </FormControl>
        </div>
      )}
    />
  );
}

export default OrganizationUserForm;
